/**
 * IndexedDB Storage Service
 * Handles persisting user designs, uploads, and settings
 *
 * Uses IndexedDB for storing:
 * - Generated designs (images as blobs)
 * - User uploads
 * - Design history
 * - Editor projects
 * - Shopping cart
 * - Order history
 *
 * Features (Phase 3):
 * - Quota monitoring with automatic cleanup when >80% full
 * - Locking for critical operations to prevent multi-tab corruption
 * - BroadcastChannel for multi-tab coordination
 */

import type { EditorSnapshot } from '@/types';
import {
  withLock,
  getStorageQuota,
  isStorageLow,
  broadcastStorageEvent,
  registerCleanupStrategy,
  type CleanupResult,
} from './storageManager';

const DB_NAME = 'engraving-pwa';
const DB_VERSION = 7;

export interface StoredDesign {
  id: string;
  userId?: string; // User ID for cloud sync (optional for backwards compatibility)
  frontBlob: Blob | null;
  backBlob: Blob | null;
  createdAt: Date;
  metadata: {
    productId: string;
    frontStyle: string;
    frontTemplate: string;
    backStyle?: string;
    backTemplate?: string;
    designName?: string; // Auto-generated name for display (e.g., "Design 2025-12-15 10:30 AM")
    // Deprecated: These fields are kept for backwards compatibility with old designs
    /** @deprecated Use frontTemplate instead */
    frontLayout?: string;
    /** @deprecated Use backTemplate instead */
    backLayout?: string;
    /** @deprecated Use frontTemplate instead */
    frontTheme?: string;
    /** @deprecated Use backTemplate instead */
    backTheme?: string;
  };
  // Layer data for re-editing (optional for backwards compatibility with old designs)
  frontLayers?: EditorSnapshot;
  backLayers?: EditorSnapshot;
}

export interface StoredProject {
  id: string;
  name: string;
  productId: string; // Product ID for filtering (required for cloud sync)
  userId?: string; // User ID for cloud sync (optional for backwards compatibility)
  snapshot: EditorSnapshot;
  thumbnailDataUrl?: string;
  createdAt: Date;
  updatedAt: Date;
}

export type DesignSource =
  | { type: 'library'; designId: string }
  | { type: 'manual'; projectId?: string }
  | { type: 'ai'; generationId?: string };

export interface CartItem {
  id: string;
  userId?: string; // User ID for cloud sync (optional for backwards compatibility)
  productId: string;
  productName: string;
  /** User's design for preview/display */
  frontDesignBlob: Blob | null;
  /** User's design for preview/display */
  backDesignBlob: Blob | null;
  /** Production-ready image data URL (with frame overlay, inverted) for laser engraving */
  frontProductionDataUrl?: string | null;
  /** Production-ready image data URL (with frame overlay, inverted) for laser engraving */
  backProductionDataUrl?: string | null;
  designThumbnail?: string;
  quantity: number;
  priceInCents: number;
  addedAt: Date;
  updatedAt?: Date; // For sync conflict resolution
  metadata: {
    mode: 'library' | 'manual' | 'ai';
    designName?: string;
    designSource: DesignSource;
  };
}

export type OrderStatus = 'pending' | 'paid' | 'failed' | 'cancelled';

/** Production status for order fulfillment tracking */
export type ProductionStatus = 'placed' | 'confirmed' | 'queued' | 'completed' | 'shipped';

export interface AutoSaveEntry {
  id: string;
  userId: string;
  productId: string;
  side: 'front' | 'back';
  snapshot: EditorSnapshot;
  createdAt: Date;
  isManualSave: boolean;
}

export interface Order {
  id: string;
  stripePaymentIntentId?: string;
  productId: string;
  productName: string;
  /** User's design for preview/display (null for cloud-sourced orders) */
  frontDesignBlob: Blob | null;
  /** User's design for preview/display */
  backDesignBlob: Blob | null;
  /** Production-ready image data URL (with frame overlay, inverted) for laser engraving */
  frontProductionDataUrl?: string | null;
  /** Production-ready image data URL (with frame overlay, inverted) for laser engraving */
  backProductionDataUrl?: string | null;
  designThumbnail?: string;
  quantity: number;
  totalPaid: number;
  status: OrderStatus;
  createdAt: Date;
  paidAt?: Date;
  metadata: {
    mode: 'library' | 'manual' | 'ai';
    designName?: string;
    customerEmail?: string;
  };
  // Extended fields for Supabase orders (URLs instead of blobs)
  orderNumber?: string;
  frontDesignUrl?: string;
  backDesignUrl?: string;
  // Production status tracking fields
  productionStatus?: ProductionStatus;
  productionStatusUpdatedAt?: Date;
  queuePosition?: number;
  estimatedDeliveryDate?: Date;
}

/**
 * Initialize the IndexedDB database
 */
export async function initDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const oldVersion = event.oldVersion;

      // Designs store (v1+)
      if (!db.objectStoreNames.contains('designs')) {
        const designStore = db.createObjectStore('designs', { keyPath: 'id' });
        designStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // Uploads store (v1+)
      if (!db.objectStoreNames.contains('uploads')) {
        db.createObjectStore('uploads', { keyPath: 'id' });
      }

      // Projects store (editor projects) (v2+)
      if (!db.objectStoreNames.contains('projects')) {
        const projectStore = db.createObjectStore('projects', { keyPath: 'id' });
        projectStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }

      // Cart store (v3+)
      if (oldVersion < 3 && !db.objectStoreNames.contains('cart')) {
        db.createObjectStore('cart', { keyPath: 'id' });
      }

      // Orders store (v3+)
      if (oldVersion < 3 && !db.objectStoreNames.contains('orders')) {
        const orderStore = db.createObjectStore('orders', { keyPath: 'id' });
        orderStore.createIndex('createdAt', 'createdAt', { unique: false });
        orderStore.createIndex('status', 'status', { unique: false });
      }

      // v3 → v4: Migrate single blob to front/back blobs
      if (oldVersion < 4) {
        const transaction = (event.target as IDBOpenDBRequest).transaction!;

        // Migrate designs store
        if (db.objectStoreNames.contains('designs')) {
          const designStore = transaction.objectStore('designs');
          designStore.openCursor().onsuccess = (e) => {
            const cursor = (e.target as IDBRequest).result;
            if (cursor) {
              const design = cursor.value;
              // Migrate old single blob format to front/back format
              if (design.blob && !design.frontBlob) {
                design.frontBlob = design.blob;
                design.backBlob = null;
                // Migrate metadata
                if (design.metadata) {
                  design.metadata.frontStyle = design.metadata.style;
                  design.metadata.frontTheme = design.metadata.theme;
                  delete design.metadata.style;
                  delete design.metadata.theme;
                }
                delete design.blob; // Remove legacy field
                cursor.update(design);
              }
              cursor.continue();
            }
          };
        }

        // Migrate cart store
        if (db.objectStoreNames.contains('cart')) {
          const cartStore = transaction.objectStore('cart');
          cartStore.openCursor().onsuccess = (e) => {
            const cursor = (e.target as IDBRequest).result;
            if (cursor) {
              const item = cursor.value;
              if (item.designBlob && !item.frontDesignBlob) {
                item.frontDesignBlob = item.designBlob;
                item.backDesignBlob = null;
                delete item.designBlob;
                cursor.update(item);
              }
              cursor.continue();
            }
          };
        }

        // Migrate orders store
        if (db.objectStoreNames.contains('orders')) {
          const orderStore = transaction.objectStore('orders');
          orderStore.openCursor().onsuccess = (e) => {
            const cursor = (e.target as IDBRequest).result;
            if (cursor) {
              const order = cursor.value;
              if (order.designBlob && !order.frontDesignBlob) {
                order.frontDesignBlob = order.designBlob;
                order.backDesignBlob = null;
                delete order.designBlob;
                cursor.update(order);
              }
              cursor.continue();
            }
          };
        }
      }

      // v4 → v5: Add auto-save history store
      if (oldVersion < 5) {
        if (!db.objectStoreNames.contains('autosave')) {
          const autosaveStore = db.createObjectStore('autosave', { keyPath: 'id' });
          autosaveStore.createIndex('userId', 'userId', { unique: false });
          autosaveStore.createIndex('createdAt', 'createdAt', { unique: false });
          autosaveStore.createIndex('userProductSide', ['userId', 'productId', 'side'], {
            unique: false,
          });
        }
      }

      // v6 → v7: Cloud sync support (no schema changes, just version bump)
      // userId and productId fields added to StoredProject/StoredDesign interfaces
      // Existing data continues to work (fields are optional)
      if (oldVersion < 7) {
        console.warn('[IndexedDB] Upgraded to v7 - cloud sync support');
      }
    };
  });
}

/**
 * Save a generated design to IndexedDB
 * Uses locking to prevent multi-tab corruption
 */
export async function saveDesign(design: StoredDesign): Promise<void> {
  // Check quota before saving
  if (await isStorageLow()) {
    console.warn('[Storage] Quota low, running cleanup before save');
    await cleanupOldDesigns(10); // Keep only 10 most recent designs
  }

  return withLock('designs', async () => {
    const db = await initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('designs', 'readwrite');
      const store = transaction.objectStore('designs');
      const request = store.put(design);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        broadcastStorageEvent('write', 'designs', design.id);
        resolve();
      };
    });
  });
}

/**
 * Cleanup old designs (keep only N most recent)
 */
async function cleanupOldDesigns(keepCount: number): Promise<number> {
  const designs = await getDesigns();
  if (designs.length <= keepCount) {
    return 0;
  }

  const db = await initDatabase();
  const toDelete = designs.slice(keepCount);

  let deleted = 0;
  for (const design of toDelete) {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction('designs', 'readwrite');
      const store = transaction.objectStore('designs');
      const request = store.delete(design.id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        deleted++;
        resolve();
      };
    });
  }

  console.warn(`[Storage] Cleaned up ${deleted} old designs`);
  return deleted;
}

/**
 * Get all saved designs, sorted by most recent first
 */
export async function getDesigns(): Promise<StoredDesign[]> {
  const db = await initDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('designs', 'readonly');
    const store = transaction.objectStore('designs');
    const index = store.index('createdAt');
    const request = index.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      // Sort by most recent first
      const designs = request.result as StoredDesign[];
      designs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      resolve(designs);
    };
  });
}

// ============================================
// Project Storage (Editor Projects)
// ============================================

/**
 * Save an editor project to IndexedDB
 * Uses locking to prevent multi-tab corruption
 */
export async function saveProject(project: StoredProject): Promise<void> {
  // Check quota before saving
  if (await isStorageLow()) {
    console.warn('[Storage] Quota low, running cleanup before project save');
    await cleanupOldProjects(10); // Keep only 10 most recent projects
  }

  return withLock('projects', async () => {
    const db = await initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('projects', 'readwrite');
      const store = transaction.objectStore('projects');
      const request = store.put(project);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        broadcastStorageEvent('write', 'projects', project.id);
        resolve();
      };
    });
  });
}

/**
 * Cleanup old projects (keep only N most recent)
 */
async function cleanupOldProjects(keepCount: number): Promise<number> {
  const projects = await getProjects();
  if (projects.length <= keepCount) {
    return 0;
  }

  const db = await initDatabase();
  const toDelete = projects.slice(keepCount);

  let deleted = 0;
  for (const project of toDelete) {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction('projects', 'readwrite');
      const store = transaction.objectStore('projects');
      const request = store.delete(project.id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        deleted++;
        resolve();
      };
    });
  }

  console.warn(`[Storage] Cleaned up ${deleted} old projects`);
  return deleted;
}

/**
 * Get a project by ID
 */
export async function getProject(id: string): Promise<StoredProject | undefined> {
  const db = await initDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('projects', 'readonly');
    const store = transaction.objectStore('projects');
    const request = store.get(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

/**
 * Get all saved projects, sorted by most recently updated
 */
export async function getProjects(): Promise<StoredProject[]> {
  const db = await initDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('projects', 'readonly');
    const store = transaction.objectStore('projects');
    const index = store.index('updatedAt');
    const request = index.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      // Sort by most recent first
      const projects = request.result as StoredProject[];
      projects.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      resolve(projects);
    };
  });
}

/**
 * Delete a project by ID
 * Uses locking to prevent multi-tab corruption
 */
export async function deleteProject(id: string): Promise<void> {
  return withLock('projects', async () => {
    const db = await initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('projects', 'readwrite');
      const store = transaction.objectStore('projects');
      const request = store.delete(id);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        broadcastStorageEvent('delete', 'projects', id);
        resolve();
      };
    });
  });
}

/**
 * Download a data URL as a file
 */
export function downloadDataUrl(dataUrl: string, filename: string): void {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// ============================================
// Cart Storage (Shopping Cart)
// ============================================

/**
 * Save cart item to IndexedDB
 * Uses locking to prevent multi-tab corruption
 */
export async function saveCartItem(item: CartItem): Promise<void> {
  return withLock('cart', async () => {
    const db = await initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('cart', 'readwrite');
      const store = transaction.objectStore('cart');
      const request = store.put(item);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        broadcastStorageEvent('write', 'cart', item.id);
        resolve();
      };
    });
  });
}

/**
 * Get all cart items
 */
export async function getCartItems(): Promise<CartItem[]> {
  const db = await initDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('cart', 'readonly');
    const store = transaction.objectStore('cart');
    const request = store.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const items = request.result as CartItem[];
      resolve(items);
    };
  });
}

/**
 * Remove a specific cart item by ID
 * Uses locking to prevent multi-tab corruption
 */
export async function removeCartItem(itemId: string): Promise<void> {
  return withLock('cart', async () => {
    const db = await initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('cart', 'readwrite');
      const store = transaction.objectStore('cart');
      const request = store.delete(itemId);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        broadcastStorageEvent('delete', 'cart', itemId);
        resolve();
      };
    });
  });
}

/**
 * Clear cart (remove all items)
 * Uses locking to prevent multi-tab corruption
 */
export async function clearCart(): Promise<void> {
  return withLock('cart', async () => {
    const db = await initDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('cart', 'readwrite');
      const store = transaction.objectStore('cart');
      const request = store.clear();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        broadcastStorageEvent('clear', 'cart');
        resolve();
      };
    });
  });
}

// ============================================
// Order Storage (Purchase History)
// ============================================

/**
 * Save an order to IndexedDB
 */
export async function saveOrder(order: Order): Promise<void> {
  const db = await initDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('orders', 'readwrite');
    const store = transaction.objectStore('orders');
    const request = store.put(order);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Get an order by ID
 */
export async function getOrder(id: string): Promise<Order | undefined> {
  const db = await initDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('orders', 'readonly');
    const store = transaction.objectStore('orders');
    const request = store.get(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

/**
 * Get all orders, sorted by most recent first
 */
export async function getOrders(): Promise<Order[]> {
  const db = await initDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('orders', 'readonly');
    const store = transaction.objectStore('orders');
    const index = store.index('createdAt');
    const request = index.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      // Sort by most recent first
      const orders = request.result as Order[];
      orders.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      resolve(orders);
    };
  });
}

/**
 * Get recent pending orders (created in last 10 minutes)
 * Used for matching Stripe payment redirects
 */
export async function getRecentPendingOrders(): Promise<Order[]> {
  const db = await initDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('orders', 'readonly');
    const store = transaction.objectStore('orders');
    const request = store.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const orders = request.result as Order[];
      const tenMinutesAgo = Date.now() - 10 * 60 * 1000;

      const recentPending = orders
        .filter((o) => o.status === 'pending' && new Date(o.createdAt).getTime() > tenMinutesAgo)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      resolve(recentPending);
    };
  });
}

/**
 * Clean up abandoned pending orders (older than 24 hours)
 * Returns the number of orders cleaned up
 */
export async function cleanupAbandonedOrders(): Promise<number> {
  const db = await initDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('orders', 'readwrite');
    const store = transaction.objectStore('orders');
    const request = store.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const orders = request.result as Order[];
      const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;

      // Find abandoned pending orders
      const abandonedOrders = orders.filter(
        (o) => o.status === 'pending' && new Date(o.createdAt).getTime() < twentyFourHoursAgo
      );

      // Delete them
      let completed = 0;
      abandonedOrders.forEach((order) => {
        const deleteRequest = store.delete(order.id);
        deleteRequest.onsuccess = () => {
          completed++;
          if (completed === abandonedOrders.length) {
            resolve(abandonedOrders.length);
          }
        };
      });

      // If no abandoned orders, resolve immediately
      if (abandonedOrders.length === 0) {
        resolve(0);
      }
    };
  });
}

/**
 * Update order status
 */
export async function updateOrderStatus(
  orderId: string,
  status: OrderStatus,
  paymentIntentId?: string
): Promise<void> {
  const db = await initDatabase();
  const order = await getOrder(orderId);

  if (!order) {
    throw new Error(`Order ${orderId} not found`);
  }

  const updatedOrder: Order = {
    ...order,
    status,
    stripePaymentIntentId: paymentIntentId || order.stripePaymentIntentId,
    paidAt: status === 'paid' ? new Date() : order.paidAt,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('orders', 'readwrite');
    const store = transaction.objectStore('orders');
    const request = store.put(updatedOrder);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Download design blob from an order
 * Downloads separate files for front and back if both exist
 */
export async function downloadOrderDesign(orderId: string): Promise<void> {
  const order = await getOrder(orderId);
  if (!order) {
    throw new Error(`Order ${orderId} not found`);
  }

  // Download front side
  if (!order.frontDesignBlob) {
    console.warn('[Storage] No front design blob to download for order:', orderId);
    return;
  }
  const frontReader = new FileReader();
  frontReader.onload = () => {
    const dataUrl = frontReader.result as string;
    const filename = order.backDesignBlob
      ? `engraving-design-front-${orderId}.png`
      : `engraving-design-${orderId}.png`;
    downloadDataUrl(dataUrl, filename);
  };
  frontReader.readAsDataURL(order.frontDesignBlob);

  // Download back side if it exists
  if (order.backDesignBlob) {
    const backReader = new FileReader();
    backReader.onload = () => {
      const dataUrl = backReader.result as string;
      const filename = `engraving-design-back-${orderId}.png`;
      downloadDataUrl(dataUrl, filename);
    };
    backReader.readAsDataURL(order.backDesignBlob);
  }
}

// ============================================
// Auto-Save Storage (IndexedDB)
// ============================================

/**
 * Save an auto-save entry to IndexedDB
 * Automatically cleans up old entries (keeps last 20)
 * Uses locking to prevent auto-save + manual save race conditions
 */
export async function saveAutoSave(entry: AutoSaveEntry): Promise<void> {
  return withLock(`autosave-${entry.productId}-${entry.side}`, async () => {
    const db = await initDatabase();

    // Save new entry
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction('autosave', 'readwrite');
      const store = transaction.objectStore('autosave');
      const request = store.put(entry);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        broadcastStorageEvent('write', 'autosave', entry.id);
        resolve();
      };
    });

    // Cleanup old auto-saves if not a manual save
    if (!entry.isManualSave) {
      await cleanupOldAutoSavesInternal(db, entry.userId, entry.productId, entry.side);
    }
  });
}

/**
 * Get all auto-saves for a user/product/side combination
 * Sorted by most recent first
 */
export async function getAutoSaves(
  userId: string,
  productId: string,
  side: 'front' | 'back',
  includeManual: boolean = true
): Promise<AutoSaveEntry[]> {
  const db = await initDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('autosave', 'readonly');
    const store = transaction.objectStore('autosave');
    const index = store.index('userProductSide');
    const request = index.getAll([userId, productId, side]);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const entries = request.result as AutoSaveEntry[];

      // Filter out manual saves if requested
      const filtered = includeManual ? entries : entries.filter((e) => !e.isManualSave);

      // Sort by most recent first
      filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      resolve(filtered);
    };
  });
}

/**
 * Delete an auto-save entry
 */
export async function deleteAutoSave(id: string): Promise<void> {
  const db = await initDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction('autosave', 'readwrite');
    const store = transaction.objectStore('autosave');
    const request = store.delete(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Internal cleanup function (used within lock)
 */
async function cleanupOldAutoSavesInternal(
  db: IDBDatabase,
  userId: string,
  productId: string,
  side: 'front' | 'back'
): Promise<number> {
  const autoSaves = await getAutoSaves(userId, productId, side, false);

  // If we have more than 20 auto-saves, delete the oldest ones
  if (autoSaves.length > 20) {
    const toDelete = autoSaves.slice(20); // Keep first 20 (most recent)

    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction('autosave', 'readwrite');
      const store = transaction.objectStore('autosave');

      let completed = 0;
      toDelete.forEach((entry) => {
        const request = store.delete(entry.id);
        request.onsuccess = () => {
          completed++;
          if (completed === toDelete.length) {
            resolve();
          }
        };
        request.onerror = () => reject(request.error);
      });
    });

    return toDelete.length;
  }

  return 0;
}

/**
 * Clean up old auto-saves (keep only 20 most recent)
 * Only affects auto-saves (isManualSave = false)
 */
export async function cleanupOldAutoSaves(
  userId: string,
  productId: string,
  side: 'front' | 'back'
): Promise<number> {
  const db = await initDatabase();
  return cleanupOldAutoSavesInternal(db, userId, productId, side);
}

/**
 * Get the most recent auto-save for a user/product/side
 */
export async function getLatestAutoSave(
  userId: string,
  productId: string,
  side: 'front' | 'back'
): Promise<AutoSaveEntry | null> {
  const autoSaves = await getAutoSaves(userId, productId, side);
  return autoSaves.length > 0 ? (autoSaves[0] ?? null) : null;
}

// ============================================
// Migration Helpers (for cloud sync)
// ============================================

/**
 * Check if a string is a valid UUID v4 format
 */
function isValidUUID(id: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
}

/**
 * Migrate designs without userId to a specific user
 * Also regenerates non-UUID IDs for Supabase compatibility
 * Used when user signs in for the first time to claim existing local designs
 * @returns Number of designs migrated
 */
export async function migrateDesignsToUser(userId: string): Promise<number> {
  const designs = await getDesigns();
  // Migrate designs that either: don't have userId OR have non-UUID IDs
  const designsToMigrate = designs.filter((d) => !d.userId || !isValidUUID(d.id));

  if (designsToMigrate.length === 0) {
    return 0;
  }

  const db = await initDatabase();
  let migrated = 0;

  for (const design of designsToMigrate) {
    const oldId = design.id;
    const needsNewId = !isValidUUID(oldId);
    const newId = needsNewId ? crypto.randomUUID() : oldId;

    const updatedDesign: StoredDesign = {
      ...design,
      id: newId,
      userId,
    };

    try {
      // If ID changed, delete old record first
      if (needsNewId) {
        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction('designs', 'readwrite');
          const store = tx.objectStore('designs');
          const deleteReq = store.delete(oldId);
          deleteReq.onsuccess = () => resolve();
          deleteReq.onerror = () => reject(deleteReq.error);
        });
        console.warn(`[migrateDesignsToUser] Regenerated ID: ${oldId} → ${newId}`);
      }

      // Save with new/updated data
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction('designs', 'readwrite');
        const store = tx.objectStore('designs');
        const putReq = store.put(updatedDesign);
        putReq.onsuccess = () => resolve();
        putReq.onerror = () => reject(putReq.error);
      });

      migrated++;
    } catch (error) {
      console.error('[migrateDesignsToUser] Failed to migrate design:', oldId, error);
    }
  }

  return migrated;
}

/**
 * Migrate projects without userId to a specific user
 * Also regenerates non-UUID IDs for Supabase compatibility
 * Used when user signs in for the first time to claim existing local projects
 * @returns Number of projects migrated
 */
export async function migrateProjectsToUser(userId: string): Promise<number> {
  const projects = await getProjects();
  // Migrate projects that either: don't have userId OR have non-UUID IDs
  const projectsToMigrate = projects.filter((p) => !p.userId || !isValidUUID(p.id));

  if (projectsToMigrate.length === 0) {
    return 0;
  }

  const db = await initDatabase();
  let migrated = 0;

  for (const project of projectsToMigrate) {
    const oldId = project.id;
    const needsNewId = !isValidUUID(oldId);
    const newId = needsNewId ? crypto.randomUUID() : oldId;

    const updatedProject: StoredProject = {
      ...project,
      id: newId,
      userId,
    };

    try {
      // If ID changed, delete old record first
      if (needsNewId) {
        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction('projects', 'readwrite');
          const store = tx.objectStore('projects');
          const deleteReq = store.delete(oldId);
          deleteReq.onsuccess = () => resolve();
          deleteReq.onerror = () => reject(deleteReq.error);
        });
        console.warn(`[migrateProjectsToUser] Regenerated ID: ${oldId} → ${newId}`);
      }

      // Save with new/updated data
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction('projects', 'readwrite');
        const store = tx.objectStore('projects');
        const putReq = store.put(updatedProject);
        putReq.onsuccess = () => resolve();
        putReq.onerror = () => reject(putReq.error);
      });

      migrated++;
    } catch (error) {
      console.error('[migrateProjectsToUser] Failed to migrate project:', oldId, error);
    }
  }

  return migrated;
}

/**
 * Migrate cart items without userId to a specific user
 * Also regenerates non-UUID IDs for Supabase compatibility
 * Used when user signs in to claim existing local cart items
 * @returns Number of cart items migrated
 */
export async function migrateCartToUser(userId: string): Promise<number> {
  const cartItems = await getCartItems();
  // Migrate cart items that either: don't have userId OR have non-UUID IDs
  const itemsToMigrate = cartItems.filter((item) => !item.userId || !isValidUUID(item.id));

  if (itemsToMigrate.length === 0) {
    return 0;
  }

  const db = await initDatabase();
  let migrated = 0;

  for (const item of itemsToMigrate) {
    const oldId = item.id;
    const needsNewId = !isValidUUID(oldId);
    const newId = needsNewId ? crypto.randomUUID() : oldId;

    const updatedItem: CartItem = {
      ...item,
      id: newId,
      userId,
      updatedAt: new Date(),
    };

    try {
      // If ID changed, delete old record first
      if (needsNewId) {
        await new Promise<void>((resolve, reject) => {
          const tx = db.transaction('cart', 'readwrite');
          const store = tx.objectStore('cart');
          const deleteReq = store.delete(oldId);
          deleteReq.onsuccess = () => resolve();
          deleteReq.onerror = () => reject(deleteReq.error);
        });
        console.warn(`[migrateCartToUser] Regenerated ID: ${oldId} → ${newId}`);
      }

      // Save with new/updated data
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction('cart', 'readwrite');
        const store = tx.objectStore('cart');
        const putReq = store.put(updatedItem);
        putReq.onsuccess = () => resolve();
        putReq.onerror = () => reject(putReq.error);
      });

      migrated++;
    } catch (error) {
      console.error('[migrateCartToUser] Failed to migrate cart item:', oldId, error);
    }
  }

  return migrated;
}

// ============================================
// Quota Management & Cleanup Strategies
// ============================================

/**
 * Get current storage quota information
 */
export { getStorageQuota, isStorageLow };

/**
 * Register cleanup strategies for quota management
 * Called during app initialization
 */
export function registerStorageCleanupStrategies(): void {
  // Cleanup strategy for designs store
  registerCleanupStrategy('designs', async (db: IDBDatabase): Promise<CleanupResult[]> => {
    const results: CleanupResult[] = [];

    // Get all designs and keep only 10 most recent
    const designs = await getDesigns();
    if (designs.length > 10) {
      const toDelete = designs.slice(10);
      let freedBytes = 0;

      for (const design of toDelete) {
        // Estimate size of design (rough calculation)
        const frontSize = design.frontBlob?.size || 0;
        const backSize = design.backBlob?.size || 0;
        freedBytes += frontSize + backSize + 1000; // Add ~1KB for metadata

        await new Promise<void>((resolve, reject) => {
          const transaction = db.transaction('designs', 'readwrite');
          const store = transaction.objectStore('designs');
          const request = store.delete(design.id);
          request.onerror = () => reject(request.error);
          request.onsuccess = () => resolve();
        });
      }

      results.push({
        store: 'designs',
        deletedCount: toDelete.length,
        freedBytes,
      });
    }

    return results;
  });

  // Cleanup strategy for projects store
  registerCleanupStrategy('projects', async (db: IDBDatabase): Promise<CleanupResult[]> => {
    const results: CleanupResult[] = [];

    const projects = await getProjects();
    if (projects.length > 10) {
      const toDelete = projects.slice(10);
      let freedBytes = 0;

      for (const project of toDelete) {
        // Estimate size (~10KB per project on average)
        freedBytes += 10000;

        await new Promise<void>((resolve, reject) => {
          const transaction = db.transaction('projects', 'readwrite');
          const store = transaction.objectStore('projects');
          const request = store.delete(project.id);
          request.onerror = () => reject(request.error);
          request.onsuccess = () => resolve();
        });
      }

      results.push({
        store: 'projects',
        deletedCount: toDelete.length,
        freedBytes,
      });
    }

    return results;
  });

  console.warn('[Storage] Cleanup strategies registered');
}
